import React, { useCallback, useEffect, useState } from 'react';
import AppIntersectionObserver from '../app/appIntersectionObserver';
import AppLoading from '../app/appLoading';
import { AppNoFeedback } from '../app/appNoFeedback';
import { Box, Theme, useTheme } from '@mui/material';
import { ReportsProjectItem } from './reportsProjectItem';
import { useProjectsSummary } from '../../hooks/reports/useProjectsSummary';
import { ColorPalette, TypographyFamily } from '../../styles/constants';
import { useReportsFilters } from '../../context/reports/useReportsCtx';
import { DEFAULT_PAGE_SIZE } from '../../constants';

interface Props {
  viewUnloggedProjects: boolean;
}

const makeStyles = (theme: Theme) => ({
  topBox: { display: 'flex', flexDirection: 'column', width: '100%' },
  allocationsRow: {
    mb: 2,
  },
  allocationsBtn: {
    textDecorationColor: ColorPalette.GREEN,
    fontFamily: TypographyFamily.NEXA,
  },
  backBtnContent: { display: 'flex', alignItems: 'center', fontSize: 16 },
  mobileItemsRow: { display: 'flex', flexDirection: 'column' },
  mobileViewsBox: { pt: 2, pr: 1, pl: 1 },
  backIcon: {
    mr: 2,
    fill: `url(#my-cool-gradient) ${theme.palette.primary.main}`,
  },
});

export function ReportsProjectList({
  viewUnloggedProjects,
}: Props): JSX.Element {
  const theme = useTheme();
  const styles = makeStyles(theme);

  const [skip, setSkip] = useState(0);
  const filters = useReportsFilters();
  const { items, isLoading, isFetching, fetchData, isEmpty, hasMore } =
    useProjectsSummary();

  useEffect(() => {
    if (filters) {
      fetchData(filters, viewUnloggedProjects);
      setSkip(0);
    }
  }, [filters, viewUnloggedProjects, fetchData]);

  const handleObserver = useCallback(() => {
    if (isLoading || isFetching || !hasMore || !filters) {
      return;
    }

    fetchData(
      { ...filters, skip: skip + DEFAULT_PAGE_SIZE },
      viewUnloggedProjects
    );
    setSkip(skip + DEFAULT_PAGE_SIZE);
  }, [
    isLoading,
    isFetching,
    hasMore,
    filters,
    fetchData,
    skip,
    viewUnloggedProjects,
  ]);

  return (
    <Box sx={styles.topBox}>
      {!isEmpty &&
        !isLoading &&
        filters &&
        items.map((item) => {
          return (
            <ReportsProjectItem
              data={item}
              startDateFilter={filters.startDate}
              endDateFilter={filters.endDate}
              employeeIdFilter={filters.employeeId}
              key={item.project.id}
            />
          );
        })}

      <AppIntersectionObserver intersect={handleObserver} />

      {(isLoading || isFetching) && <AppLoading />}
      {isEmpty && <AppNoFeedback info="data" />}
    </Box>
  );
}
